/* eslint-disable import/prefer-default-export */

import { GlobalState } from 'components/types';
import { carryForwardModeOfColumns } from 'components/utils/carryForwardOnContinue';
import { getUsableCapacityInitialValue, itemValueOrEmpty } from '../utils/capacityFallback';
import { landCarriedValues } from '../utils/carryForward';
import { buildCustomOperatorFieldDefaults } from '../utils/customOperatorFieldDefaults';
import { FormValues } from './types';

export const getDefaultFormValues = (
  t: any,
  itemData: Record<string, any>,
  columnMappings: any, // New mappingConfig.columns format
  globalState: GlobalState,
  previousItemFormValues: Partial<FormValues> | undefined,
  fillingStatusFromValue: (value: any) => string,
  customOperatorFields: string[] = [],
  carryForwardModeOf: (settingKey: string) => string = carryForwardModeOfColumns(columnMappings),
): FormValues => {
  const getCsv = (key: string) => columnMappings?.[key]?.mapping?.csvName;
  const getUnit = (key: string, fallback = '') => (columnMappings?.[key]?.mapping?.unit || fallback);

  const fillingCsv = getCsv('filling');
  const fillingStatus = fillingCsv ? fillingStatusFromValue(itemData[fillingCsv]) : 'full';
  const isEmpty = fillingStatus === 'empty';

  const totalCapacityCsv = getCsv('totalCapacity');
  const usableCapacityCsv = getCsv('usableCapacity');
  const totalCapacityUnit = getUnit('totalCapacity', 'L');
  const usableCapacityUnit = getUnit('usableCapacity', 'L');
  const liquidVolumeCsv = getCsv('liquidVolume');
  const tavCsv = getCsv('tav');
  const tavTemperatureCsv = getCsv('tavTemperature');
  const volumeTemperatureCsv = getCsv('volumeTemperature');
  const heightUnderBungCsv = getCsv('heightUnderBung');
  const liquidHeightCsv = getCsv('liquidHeight');
  const emptyHeightCsv = getCsv('emptyHeight');

  const storedValues: FormValues = {
    totalCapacity: {
      value: itemValueOrEmpty(itemData, totalCapacityCsv),
      unit: totalCapacityUnit,
    },
    usableCapacity: {
      value: getUsableCapacityInitialValue({
        itemData,
        fillingStatus,
        usableCapacityCsv,
        totalCapacityCsv,
        usableCapacityUnit,
        totalCapacityUnit,
      }),
      unit: usableCapacityUnit,
    },
    liquidVolume: {
      value: itemValueOrEmpty(itemData, liquidVolumeCsv),
      unit: getUnit('liquidVolume', 'L'),
    },
    tav: { value: itemValueOrEmpty(itemData, tavCsv), unit: globalState.userUnits.alcoholUnit },
    tavTemp: { value: itemValueOrEmpty(itemData, tavTemperatureCsv), unit: globalState.userUnits.temperatureUnit },
    volumeTemp: {
      value: itemValueOrEmpty(itemData, volumeTemperatureCsv),
      unit: globalState.userUnits.temperatureUnit,
    },
    diameterBond: {
      value: itemValueOrEmpty(itemData, heightUnderBungCsv),
      unit: getUnit('heightUnderBung', 'cm'),
    },
    liquidHeight: {
      value: itemValueOrEmpty(itemData, liquidHeightCsv),
      unit: getUnit('heightUnderBung', 'cm'),
    },
    emptyHeight: {
      value: itemValueOrEmpty(itemData, emptyHeightCsv),
      unit: getUnit('heightUnderBung', 'cm'),
    },
    ...buildCustomOperatorFieldDefaults({
      itemData,
      columnMappings,
      customOperatorFields,
    }),
  };

  return landCarriedValues({
    storedValues,
    carriedValues: previousItemFormValues,
    carryForwardModeOf,
    customFields: customOperatorFields,
    carryMeasures: !isEmpty,
    isFull: fillingStatus === 'full',
  }) as FormValues;
};
